// Sourced from publicly available Britannica Learning lesson catalog (teachbritannica.com).
// Each Pressto writing activity pairs with a real Britannica Learning lesson.

export interface BritannicaLesson {
  program: string;
  britannicaTitle: string;
  grade: string;
  area: string;
  title: string;
  prompt: string;
  plan: string;
  purpose: string;
  importantWords: string[];
  standards: string[];
  templateUrl: string;
}

export interface BritannicaPublisher {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  gradeRange: string;
  lessonCount: number;
  areaCount: number;
  areas: string[];
  lessons: BritannicaLesson[];
}

export const BRITANNICA_PUBLISHER: BritannicaPublisher = {
  "slug": "britannica",
  "name": "Britannica Learning",
  "tagline": "Pressto + Britannica Learning",
  "description": "Britannica gives students the knowledge. Pressto is where they write about it. Together: standards-aligned writing activities across science, history, civics, and social studies for grades 3–8.",
  "gradeRange": "3–8",
  "lessonCount": 33,
  "areaCount": 5,
  "areas": [
    "Science",
    "History",
    "Civics",
    "Social Studies",
    "ELA"
  ],
  "lessons": [
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Energy Flow in Living Things",
      "grade": "Grades 3–5",
      "area": "Science",
      "title": "How Does Energy Travel Through a Food Chain?",
      "prompt": "In Britannica, you learned how energy moves from the sun to plants, then to animals, and beyond. Think about the steps in a food chain you explored. Write a sequence piece that explains, step by step, how energy flows through a living ecosystem. Use specific examples of producers, consumers, and decomposers to show each stage of the journey.",
      "plan": "sequence",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "food chain",
        "producer",
        "consumer",
        "decomposer",
        "ecosystem",
        "energy",
        "photosynthesis",
        "herbivore",
        "carnivore",
        "nutrient"
      ],
      "standards": [
        "W.4.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Energy in Motion",
      "grade": "Grades 3–5",
      "area": "Science",
      "title": "What Happens When Objects Collide and Energy Changes Form?",
      "prompt": "After reading about kinetic and potential energy in Britannica, you saw how energy can change from one form to another. Think about a specific example — like a ball rolling down a hill or a stretched rubber band snapping forward. Write a cause-and-effect piece that explains what causes energy to change form and what effects that change creates. Use the science vocabulary you learned to describe the relationship clearly.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "kinetic energy",
        "potential energy",
        "motion",
        "force",
        "collision",
        "transfer",
        "speed",
        "friction",
        "stored energy",
        "work"
      ],
      "standards": [
        "W.4.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Forces of Magnetism",
      "grade": "Grades 3–5",
      "area": "Science",
      "title": "Describe the Invisible Force That Pulls and Pushes",
      "prompt": "In Britannica, you explored how magnets attract and repel objects without even touching them. Think about the properties of magnets you discovered — their poles, their fields, and the materials they interact with. Write a detailed description piece that explains what magnetism is and how it works. Help a reader who has never learned about magnets understand this invisible force through vivid, precise details.",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "magnet",
        "magnetic field",
        "attract",
        "repel",
        "north pole",
        "south pole",
        "iron",
        "force",
        "compass",
        "conductor"
      ],
      "standards": [
        "W.4.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Dinosaur Exploration and Classification",
      "grade": "Grades 3–5",
      "area": "Science",
      "title": "How Are Two Dinosaurs Both Different and Surprisingly Similar?",
      "prompt": "Britannica introduced you to many types of dinosaurs — from massive plant-eaters to fierce predators. Choose two dinosaurs you found interesting and compare them closely. Write a compare-and-contrast piece that examines their size, diet, physical features, and how they survived. What makes them different, and what surprising traits do they share?",
      "plan": "compare-contrast",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "carnivore",
        "herbivore",
        "fossil",
        "paleontologist",
        "species",
        "habitat",
        "predator",
        "prey",
        "extinction",
        "classification"
      ],
      "standards": [
        "W.4.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Earth Helpers: Take Action",
      "grade": "Grades 3–5",
      "area": "Science",
      "title": "What Is One Earth Problem You Can Help Solve?",
      "prompt": "Britannica showed you real environmental problems facing our planet — from pollution to habitat loss. Now it is your turn to take action with your words. Choose one problem that matters to you and write a piece that clearly describes the problem and proposes a specific solution that kids your age could help carry out. Use evidence and details from what you read to convince your reader that your solution could really work.",
      "plan": "problem-solution",
      "purpose": "Opinion/Argument Writing",
      "importantWords": [
        "pollution",
        "habitat",
        "conservation",
        "renewable",
        "recycle",
        "environment",
        "impact",
        "solution",
        "sustainability",
        "action"
      ],
      "standards": [
        "W.4.1",
        "Opinion/Argument Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Creating a New Nation",
      "grade": "Grades 3–5",
      "area": "History",
      "title": "What Caused the American Colonists to Build Their Own Nation?",
      "prompt": "In Britannica, you learned about the events and decisions that led to the creation of the United States. Think about the tensions, debates, and turning points that pushed the colonists toward forming a new country. Write a cause-and-effect piece that explains the key causes that led to the creation of the new nation and the effects those events had on the people who lived through them. Use specific historical details to support your explanation.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "colony",
        "independence",
        "constitution",
        "democracy",
        "founding fathers",
        "revolution",
        "government",
        "taxation",
        "liberty",
        "representative"
      ],
      "standards": [
        "W.4.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Breaking from Britain",
      "grade": "Grades 3–5",
      "area": "History",
      "title": "Why Did the Colonists Decide to Break Free from British Rule?",
      "prompt": "After reading Britannica's lesson on the American Revolution, you discovered the many reasons colonists grew frustrated with British control. Think about the taxes, laws, and conflicts that built tension over time. Write a cause-and-effect piece that explains what caused the colonists to demand independence and what effects their decision had on both America and Britain. Use dates, events, and key figures to make your writing specific and convincing.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "taxation",
        "parliament",
        "boycott",
        "rebellion",
        "loyalist",
        "patriot",
        "declaration",
        "colony",
        "protest",
        "sovereignty"
      ],
      "standards": [
        "W.4.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Native American Leaders: Past and Present",
      "grade": "Grades 3–5",
      "area": "History",
      "title": "How Do a Historic and a Modern Native American Leader Compare?",
      "prompt": "Britannica introduced you to Native American leaders from different periods in history — some who fought for their people long ago and others who are making a difference today. Choose one leader from the past and one from the present. Write a compare-and-contrast piece that examines their challenges, goals, and methods of leadership. What has changed over time, and what values have stayed the same?",
      "plan": "compare-contrast",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "indigenous",
        "sovereignty",
        "treaty",
        "activism",
        "heritage",
        "tribe",
        "advocate",
        "resistance",
        "culture",
        "representation"
      ],
      "standards": [
        "W.4.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Celebrating Black Voices",
      "grade": "Grades 3–5",
      "area": "History",
      "title": "Describe the Life and Legacy of an Inspiring Black American",
      "prompt": "In Britannica, you read about remarkable Black Americans who used their voices, talents, and courage to make history. Choose one person who inspired you and write a rich description of who they were and what they accomplished. Include details about their background, their challenges, and the lasting impact they had on their community or country. Paint a picture with words so your reader can truly understand why this person's story matters.",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "legacy",
        "trailblazer",
        "justice",
        "community",
        "resilience",
        "achievement",
        "civil rights",
        "barrier",
        "inspire",
        "contribution"
      ],
      "standards": [
        "W.4.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "All About Me Autobiography",
      "grade": "Grades 3–5",
      "area": "ELA",
      "title": "Tell Your Own Story: Who Are You and What Matters to You?",
      "prompt": "Britannica helped you understand what an autobiography is and how writers share their own stories. Now it is time to write yours. Think about a meaningful moment in your life, a person who has shaped you, or a challenge you have overcome. Write a personal narrative that brings your story to life with specific details, feelings, and a clear sense of who you are. Let your true voice shine through every sentence.",
      "plan": "personal-narrative",
      "purpose": "Narrative Writing",
      "importantWords": [
        "autobiography",
        "memory",
        "narrative",
        "describe",
        "moment",
        "reflection",
        "experience",
        "character",
        "voice",
        "identity"
      ],
      "standards": [
        "W.4.3",
        "Narrative Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Bringing History to Life with Biographies",
      "grade": "Grades 3–5",
      "area": "ELA",
      "title": "Retell a Historic Figure's Life as If You Were There",
      "prompt": "After exploring biographies in Britannica, you learned how authors research and retell the lives of real people who shaped history. Choose a historical figure from the lesson and retell the key events of their life in order, from an early challenge they faced to a major accomplishment they achieved. Use vivid details and specific moments to make this person feel real and present to your reader. Show why their story is worth remembering.",
      "plan": "retell-the-narrative",
      "purpose": "Narrative Writing",
      "importantWords": [
        "biography",
        "chronological",
        "milestone",
        "perseverance",
        "influence",
        "primary source",
        "historical",
        "achievement",
        "turning point",
        "legacy"
      ],
      "standards": [
        "W.4.3",
        "Narrative Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Introduction to Civics",
      "grade": "Grades 6–8",
      "area": "Civics",
      "title": "Should Citizens Be Required to Vote in Elections?",
      "prompt": "In your Britannica lesson, you explored what civics means and why an informed, engaged citizenry matters to a healthy democracy. Citizens have rights, but they also have responsibilities — and voting is one of the most important. Think about the arguments for and against making voting mandatory. Write an op-ed sharing your opinion on whether voting should be required by law, using evidence and reasoning to persuade your reader.",
      "plan": "op-ed",
      "purpose": "Opinion/Argument Writing",
      "importantWords": [
        "civics",
        "democracy",
        "citizenship",
        "rights",
        "responsibilities",
        "civic engagement",
        "legislation",
        "suffrage",
        "representation",
        "constitution"
      ],
      "standards": [
        "W.7.1",
        "Opinion/Argument Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Women Shaping History and Society",
      "grade": "Grades 6–8",
      "area": "History",
      "title": "How Did Women Change the World Around Them?",
      "prompt": "Your Britannica lesson highlighted women throughout history who challenged barriers and transformed society through their actions and ideas. From politics to science to social movements, women's contributions have had lasting effects on the world. Choose one woman or one movement you learned about and explain what caused her rise to prominence and what effects her work had on society. Use specific details from your research to support your explanation.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "suffrage",
        "activism",
        "discrimination",
        "equality",
        "pioneer",
        "reform",
        "influence",
        "legacy",
        "barrier",
        "movement"
      ],
      "standards": [
        "W.7.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Celebrating Black Leadership and Civil Rights",
      "grade": "Grades 6–8",
      "area": "History",
      "title": "What Caused the Civil Rights Movement to Transform America?",
      "prompt": "Through your Britannica lesson, you studied Black leaders and the Civil Rights Movement — a powerful force that changed laws, challenged injustice, and reshaped American society. The movement did not happen by accident; it grew out of specific historical causes and produced far-reaching effects. Choose a key event, leader, or turning point from the Civil Rights era and explain the causes that led to it and the effects it produced. Support your writing with evidence from what you read.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "segregation",
        "nonviolent resistance",
        "legislation",
        "civil rights",
        "protest",
        "injustice",
        "activism",
        "equality",
        "systemic",
        "landmark"
      ],
      "standards": [
        "W.7.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Patterns in the Past: Fossils and Evolution",
      "grade": "Grades 6–8",
      "area": "Science",
      "title": "How Do Scientists Trace the Story of Life on Earth?",
      "prompt": "Your Britannica lesson traced how fossils provide clues about Earth's ancient past and how species change over time through evolution. Scientists follow a careful process when they study fossils — from discovery to analysis to drawing conclusions. Describe the sequence of steps involved in how a fossil is formed and then how scientists use it to understand evolution. Use precise scientific vocabulary and put each stage in the correct order.",
      "plan": "sequence",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "fossil",
        "evolution",
        "natural selection",
        "sediment",
        "paleontologist",
        "adaptation",
        "species",
        "geological record",
        "extinction",
        "trait"
      ],
      "standards": [
        "W.7.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Exploring Space with Astronauts",
      "grade": "Grades 6–8",
      "area": "Science",
      "title": "What Is Life Really Like for an Astronaut in Space?",
      "prompt": "In your Britannica lesson, you discovered what astronauts experience as they train for and live in space — from the physical challenges of microgravity to the daily routines aboard the International Space Station. Space exploration is one of humanity's greatest achievements, and the people who venture beyond Earth face a unique and demanding environment. Describe what life is like for an astronaut in space, including the conditions, challenges, and daily experiences. Use specific details from your research to paint a vivid and accurate picture.",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "microgravity",
        "orbit",
        "International Space Station",
        "astronaut",
        "atmosphere",
        "mission",
        "spacewalk",
        "module",
        "payload",
        "launch"
      ],
      "standards": [
        "W.7.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Exploring Glaciers with Glaciologists",
      "grade": "Grades 6–8",
      "area": "Science",
      "title": "What Happens When the World's Glaciers Disappear?",
      "prompt": "Your Britannica lesson followed glaciologists — scientists who study glaciers — as they investigated how these massive bodies of ice form, move, and melt. Glaciers have shaped landscapes for millions of years, but today they are shrinking at alarming rates. Explain what is causing glacier retreat and what effects this is having on the environment, sea levels, and communities around the world. Draw on specific evidence from your research to make your explanation clear and convincing.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "glacier",
        "glaciologist",
        "climate change",
        "retreat",
        "meltwater",
        "ice sheet",
        "sea level rise",
        "permafrost",
        "erosion",
        "carbon emissions"
      ],
      "standards": [
        "W.7.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Exploring Volcanoes with Volcanologists",
      "grade": "Grades 6–8",
      "area": "Science",
      "title": "What Forces Beneath Earth's Surface Trigger Volcanic Eruptions?",
      "prompt": "In your Britannica lesson, you explored how volcanologists study some of the most powerful forces on Earth — volcanic eruptions that reshape landscapes and affect millions of lives. Volcanoes don't erupt randomly; they are driven by geological processes deep within the planet. Explain what causes a volcano to erupt and what effects that eruption has on the surrounding environment and human communities. Use specific scientific vocabulary and evidence from your research.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "volcano",
        "volcanologist",
        "magma",
        "lava",
        "tectonic plates",
        "eruption",
        "pyroclastic",
        "seismic activity",
        "ash cloud",
        "caldera"
      ],
      "standards": [
        "W.7.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Earth Day: Taking Action",
      "grade": "Grades 6–8",
      "area": "Science",
      "title": "What Should Schools Do to Protect the Environment Right Now?",
      "prompt": "Your Britannica lesson explored the history of Earth Day and the urgent environmental problems our planet faces today — from climate change to pollution to habitat loss. Earth Day reminds us that action is needed, but action has to start somewhere. Write an op-ed arguing for one specific action your school or community should take to address an environmental problem. Clearly identify the problem and persuade your reader that your solution is the right one.",
      "plan": "problem-solution",
      "purpose": "Opinion/Argument Writing",
      "importantWords": [
        "sustainability",
        "pollution",
        "climate change",
        "conservation",
        "renewable energy",
        "carbon footprint",
        "biodiversity",
        "advocacy",
        "ecosystem",
        "environmental justice"
      ],
      "standards": [
        "W.7.1",
        "Opinion/Argument Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Architectural Wonders Unveiled",
      "grade": "Grades 6–8",
      "area": "Social Studies",
      "title": "What Makes a Building More Than Just a Structure?",
      "prompt": "Through your Britannica lesson, you explored some of the world's most remarkable architectural achievements — structures that have inspired awe, served important purposes, and stood the test of time. Architecture tells us about the cultures, values, and technologies of the people who built it. Choose one architectural wonder you learned about and write a detailed description of it — its key features, the materials and methods used to build it, and what makes it significant. Help your reader see and understand exactly what makes it extraordinary.",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "architecture",
        "monument",
        "engineering",
        "structure",
        "facade",
        "blueprint",
        "landmark",
        "symmetry",
        "construction",
        "legacy"
      ],
      "standards": [
        "W.7.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Museum Discovery and Exploration",
      "grade": "Grades 6–8",
      "area": "Social Studies",
      "title": "Should Museums Return Artifacts to Their Countries of Origin?",
      "prompt": "Your Britannica lesson took you inside museums around the world — institutions that preserve history, art, and culture for the public. But museums also raise important questions: Who gets to display historical objects, and who should own them? Many artifacts held in major museums were taken from other countries during colonial periods. Write an op-ed arguing whether museums should be required to return these objects to their countries of origin, using evidence and reasoning to support your position.",
      "plan": "op-ed",
      "purpose": "Opinion/Argument Writing",
      "importantWords": [
        "artifact",
        "provenance",
        "repatriation",
        "cultural heritage",
        "curator",
        "colonialism",
        "preservation",
        "exhibition",
        "ownership",
        "ethics"
      ],
      "standards": [
        "W.7.1",
        "Opinion/Argument Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Trial of John Peter Zenger",
      "grade": "Grades 6–8",
      "area": "History",
      "title": "Was the Zenger Verdict a Victory for Freedom of the Press?",
      "prompt": "In your Britannica lesson, you examined the 1735 trial of John Peter Zenger — a landmark case in which a printer was tried for publishing criticism of a colonial governor. The jury's not-guilty verdict is seen as an early milestone in the fight for freedom of the press in America. Write an op-ed arguing whether you believe the Zenger verdict was truly a victory for free speech or simply a lucky outcome that could have gone the other way. Use evidence from the trial and its historical context to make your case.",
      "plan": "op-ed",
      "purpose": "Opinion/Argument Writing",
      "importantWords": [
        "freedom of the press",
        "libel",
        "censorship",
        "colonial",
        "verdict",
        "jury",
        "First Amendment",
        "sedition",
        "journalism",
        "precedent"
      ],
      "standards": [
        "W.7.1",
        "Opinion/Argument Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Electromagnetic Spectrum",
      "grade": "Grades 6–8",
      "area": "Science",
      "title": "What Makes the Electromagnetic Spectrum So Powerful?",
      "prompt": "The electromagnetic spectrum includes all types of electromagnetic radiation, from radio waves to gamma rays. After exploring this topic in Britannica, describe the electromagnetic spectrum and explain how its different regions are organized. Choose two or three types of waves and explain their key properties and how humans use them in everyday life. Use specific details and vocabulary from your research to make your description clear and accurate.",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "electromagnetic radiation",
        "wavelength",
        "frequency",
        "visible light",
        "infrared",
        "ultraviolet",
        "gamma rays",
        "radio waves",
        "spectrum"
      ],
      "standards": [
        "W.7.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Infectious Disease",
      "grade": "Grades 6–8",
      "area": "Science",
      "title": "How Do Pathogens Spread and What Happens to the Body?",
      "prompt": "Infectious diseases are caused by pathogens such as bacteria, viruses, fungi, and parasites that can spread from person to person or through the environment. After reading about infectious disease in Britannica, explain the causes of infectious disease and trace the effects they have on the human body and on communities. Consider how pathogens enter the body, how the immune system responds, and what happens when a disease spreads through a population. Use evidence and specific examples from your research to support your explanation.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "pathogen",
        "bacteria",
        "virus",
        "immune system",
        "transmission",
        "infection",
        "epidemic",
        "antibody",
        "contagious"
      ],
      "standards": [
        "W.7.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Empires",
      "grade": "Grades 6–8",
      "area": "History",
      "title": "How Were Two Great Empires Alike and Different?",
      "prompt": "Throughout history, empires have risen and fallen, each shaped by unique geography, leadership, and culture. After studying empires in Britannica, choose two empires from different times or places and compare and contrast how they were built, governed, and how they eventually declined. Think about what made each empire powerful and what challenges each one faced. Use specific details from your research to show both the similarities and the important differences between them.",
      "plan": "compare-contrast",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "empire",
        "conquest",
        "sovereignty",
        "territory",
        "civilization",
        "decline",
        "governance",
        "colonization",
        "legacy"
      ],
      "standards": [
        "W.7.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Ecosystems",
      "grade": "Grades 6–8",
      "area": "Science",
      "title": "What Happens When One Part of an Ecosystem Changes?",
      "prompt": "An ecosystem is a community of living things interacting with each other and with their nonliving environment. After exploring ecosystems in Britannica, explain how changes in one part of an ecosystem can cause a chain of effects throughout the entire system. You might focus on what happens when a keystone species disappears, when a new species is introduced, or when a natural disaster alters the habitat. Use specific examples and scientific vocabulary to show how cause and effect work in ecosystems.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "ecosystem",
        "biodiversity",
        "food web",
        "habitat",
        "producer",
        "consumer",
        "decomposer",
        "keystone species",
        "abiotic"
      ],
      "standards": [
        "W.7.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Explore Ancient Greece",
      "grade": "Grades 6–8",
      "area": "History",
      "title": "What Made Ancient Greece One of History's Most Influential Civilizations?",
      "prompt": "Ancient Greece was a collection of city-states that made lasting contributions to democracy, philosophy, art, and science. After exploring Ancient Greece in Britannica, describe what life and society were like in this remarkable civilization. Focus on two or three key features — such as city-state government, religion, or intellectual achievements — and explain what made each one significant. Use specific details and examples from your research to bring ancient Greek civilization to life for your reader.",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "city-state",
        "democracy",
        "philosophy",
        "mythology",
        "acropolis",
        "oligarchy",
        "Hellenistic",
        "Athens",
        "Sparta"
      ],
      "standards": [
        "W.7.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Animal Migration",
      "grade": "Grades 3–5",
      "area": "Science",
      "title": "Why Do Animals Travel So Far Every Year?",
      "prompt": "Every year, millions of animals make incredible journeys called migration. After learning about animal migration in Britannica, explain what causes animals to migrate and what effects migration has on their survival. Think about the reasons animals leave their homes, such as changes in weather or food supply, and what happens to them because they migrate. Pick one animal to use as your main example and use facts from your research to explain the causes and effects clearly.",
      "plan": "cause-effect",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "migration",
        "habitat",
        "instinct",
        "navigation",
        "season",
        "route",
        "flock",
        "survive",
        "breeding"
      ],
      "standards": [
        "W.4.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Human Impacts on Earth Systems",
      "grade": "Grades 6–8",
      "area": "Science",
      "title": "What Is One Big Environmental Problem and How Can We Solve It?",
      "prompt": "Human activities such as burning fossil fuels, deforestation, and pollution are changing Earth's land, water, atmosphere, and living systems in significant ways. After reading about human impacts on Earth systems in Britannica, identify one major environmental problem and propose evidence-based solutions. Explain why the problem is serious and what will happen if it is not addressed. Then describe at least two realistic solutions and explain why they could be effective.",
      "plan": "problem-solution",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "fossil fuels",
        "deforestation",
        "greenhouse gases",
        "biodiversity",
        "sustainability",
        "pollution",
        "climate change",
        "carbon footprint",
        "renewable energy"
      ],
      "standards": [
        "W.7.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Women of the American Revolution",
      "grade": "Grades 3–5",
      "area": "History",
      "title": "Tell the Story of a Brave Woman Who Shaped the Revolution",
      "prompt": "Many women played important roles during the American Revolution, even though their stories are not always remembered. After learning about women of the American Revolution in Britannica, retell the true story of one woman who made a difference. Tell what happened to her in order, including what challenges she faced and what she did that was brave or important. Use details from your research to help your reader feel like they were there.",
      "plan": "retell-the-narrative",
      "purpose": "Narrative Writing",
      "importantWords": [
        "revolution",
        "patriot",
        "loyalist",
        "colonial",
        "courage",
        "contribute",
        "hardship",
        "independence",
        "sacrifice"
      ],
      "standards": [
        "W.4.3",
        "Narrative Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "The Solar System",
      "grade": "Grades 3–5",
      "area": "Science",
      "title": "What Is the Solar System and What Makes It So Amazing?",
      "prompt": "The solar system is made up of the sun, eight planets, moons, asteroids, and other objects that travel through space. After exploring the solar system in Britannica, describe what the solar system looks like and what makes it special. Choose two or three objects in the solar system — like planets, moons, or the sun — and explain what they are like and what makes them unique. Use facts and details from your research to help your reader picture the solar system clearly.",
      "plan": "description",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "orbit",
        "planet",
        "asteroid",
        "gravity",
        "atmosphere",
        "telescope",
        "solar",
        "rotation",
        "moon"
      ],
      "standards": [
        "W.4.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Getting Money Smart",
      "grade": "Grades 3–5",
      "area": "Social Studies",
      "title": "Should Kids Learn How to Manage Money in School?",
      "prompt": "Knowing how to earn, save, and spend money wisely is an important life skill. After learning about money and finances in Britannica, write your opinion about whether kids should be taught money management skills in school. State your opinion clearly and give at least two strong reasons to support it. Use facts and examples from your research to make your argument convincing.",
      "plan": "op-ed",
      "purpose": "Opinion/Argument Writing",
      "importantWords": [
        "budget",
        "savings",
        "income",
        "expense",
        "interest",
        "invest",
        "financial",
        "consumer",
        "responsibility"
      ],
      "standards": [
        "W.4.1",
        "Opinion/Argument Writing"
      ],
      "templateUrl": ""
    },
    {
      "program": "Britannica Learning",
      "britannicaTitle": "Eclipses of the Sun and Moon",
      "grade": "Grades 3–5",
      "area": "Science",
      "title": "How Does a Solar or Lunar Eclipse Happen, Step by Step?",
      "prompt": "Eclipses happen when the sun, moon, and Earth line up in just the right way. After learning about eclipses in Britannica, explain the steps that cause a solar eclipse or a lunar eclipse to happen. Make sure to describe each step in the correct order, starting with how the three objects begin to align and ending with what observers on Earth see. Use clear transition words and science vocabulary from your research to make each step easy to follow.",
      "plan": "sequence",
      "purpose": "Informative/Explanatory Writing",
      "importantWords": [
        "eclipse",
        "lunar",
        "solar",
        "shadow",
        "orbit",
        "alignment",
        "umbra",
        "penumbra",
        "phase"
      ],
      "standards": [
        "W.4.2",
        "Informative/Explanatory Writing"
      ],
      "templateUrl": ""
    }
  ]
};
